import { memo, useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import TableHead from "ds/components/Table/Head";
import { getHeadColumnKey } from "ds/components/Table/Head/helpers";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";

import { TableHeadInteractiveColumnProps } from "../Table/Head/InteractiveColumn";
import TableSettings from "../Table/Settings";
import { TableSettingsDrawerProps } from "../Table/Settings/DrawerContent";
import { SelectAllOption, TableColumnMeta } from "../Table/types";
import TableSimpleHeadColumn from "./HeadColumn";
import { isStickyLeft, isStickyRight } from "./helpers";
import useTableSimpleEffectiveColumnPinning from "./hooks/useEffectiveColumnPinning";
import { showTableSimpleSettingsDrawer } from "./SettingsDrawer";
import { StickyRightPredicate, TableSimpleSelectionStore, TableSimpleStore } from "./types";

// Resize bounds a table derives from its rows rather than from its column config — a tree, whose
// first column has to fit the indentation of the deepest row it shows. Returning nothing keeps the
// column's configured bounds.
export type ColumnWidthBounds = (
  column: TableColumnMeta,
  index: number
) => Pick<TableColumnMeta, "minWidth" | "maxWidth"> | undefined;

type TableSimpleHeadProps = {
  store: TableSimpleStore;
  selectionStore?: TableSimpleSelectionStore;
  selectionOptions?: SelectAllOption[];
  ariaLabel: string;
  hideSettings?: boolean;
  stickyRight?: StickyRightPredicate;
  columnWidthBounds?: ColumnWidthBounds;
} & Pick<TableSettingsDrawerProps, "trackAnalytics"> &
  Pick<
    TableHeadInteractiveColumnProps,
    | "analyticsPage"
    | "filtersStorageKey"
    | "sortCallback"
    | "tableRef"
    | "sortConfig"
    | "externalSortManagement"
  >;

const TableSimpleHead = ({
  store,
  selectionStore,
  selectionOptions,
  ariaLabel,
  sortCallback,
  analyticsPage,
  filtersStorageKey,
  trackAnalytics,
  tableRef,
  sortConfig,
  externalSortManagement,
  hideSettings,
  stickyRight = isStickyRight,
  columnWidthBounds,
}: TableSimpleHeadProps) => {
  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  // Applied while rendering, so a column carries the right bounds from the first frame it spends in
  // its new position instead of being corrected once it's already on screen.
  const boundedColumns = useMemo(
    () =>
      columns.map((column, index) => {
        const bounds = columnWidthBounds?.(column, index);

        return bounds ? { ...column, ...bounds } : column;
      }),
    [columns, columnWidthBounds]
  );

  const { effectiveIsFirstColumnPinned, isPinningEnabled } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned,
    boundedColumns[0]?.minWidth
  );

  const showTableSettingsDrawer = useCallback(() => {
    showTableSimpleSettingsDrawer({
      store,
      trackAnalytics,
      title: `Customize ${ariaLabel}`,
    });
  }, [store, trackAnalytics, ariaLabel]);

  return (
    <TableHead>
      {boundedColumns.map((column, i) => {
        const isFirstColumn = i === 0;
        const isLastColumn = i === boundedColumns.length - 1;
        const withSelection = selectionStore && isFirstColumn && !!selectionOptions?.length;

        return (
          <TableSimpleHeadColumn
            key={getHeadColumnKey(column)}
            column={column}
            store={store}
            {...(withSelection && {
              selectionStore,
              selectionOptions,
            })}
            stickyLeft={isStickyLeft(i, effectiveIsFirstColumnPinned)}
            stickyRight={stickyRight(i, columns.length)}
            analyticsPage={analyticsPage}
            filtersStorageKey={filtersStorageKey}
            sortCallback={sortCallback}
            tableRef={tableRef}
            sortConfig={sortConfig}
            isLast={isLastColumn}
            externalSortManagement={externalSortManagement}
            isPinningEnabled={isPinningEnabled}
          >
            {isLastColumn && !hideSettings && (
              <TableSettings title={`${ariaLabel} settings`} onPress={showTableSettingsDrawer} />
            )}
          </TableSimpleHeadColumn>
        );
      })}
    </TableHead>
  );
};

TableSimpleHead.displayName = "DS.TableSimpleHead";

export default memo(TableSimpleHead) as typeof TableSimpleHead;
